"use client"

import { ChevronRight, type LucideIcon } from "lucide-react"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@uavity-core/ui/components/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@uavity-core/ui/components/sidebar"
import { Link, useLocation, useNavigate } from "@tanstack/react-router"
import { cn } from "@uavity-core/ui/lib/utils"

export function NavMain({
  items,
}: {
  items: {
    title: string
    url: string
    icon: LucideIcon
    isActive?: boolean
    items?: {
      title: string
      url: string
    }[]
  }[]
}) {
  const { state } = useSidebar()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const isNavItemActive = (item: (typeof items)[0]) => {
    if (item.title === "Flight Plans") {
      return (
        pathname.startsWith("/flight-plans") || pathname.startsWith("/flight-plan")
      )
    }
    if (item.items?.length) {
      return item.items.some((sub) => pathMatches(pathname, sub.url))
    }
    return pathMatches(pathname, item.url)
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="text-[0.65rem] font-semibold uppercase tracking-widest text-muted-foreground/80">
        Platform
      </SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => {

          const itemActive = isNavItemActive(item)
          const activeBar =
            "relative before:absolute before:left-0 before:top-1/2 before:h-5 before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-primary data-[active=true]:before:opacity-100 before:opacity-0"

          if (!item.items) return (
            <SidebarMenuButton
              asChild
              key={item.title}
              isActive={itemActive}
              className={cn(activeBar, "data-[active=true]:bg-sidebar-accent/90")}
            >
              <Link to={item.url}>
                <item.icon />
                <span>{item.title}</span>
              </Link>
            </SidebarMenuButton>
          )
          if (item.items) return (
            <Collapsible
              key={`${item.title}-${pathname}`}
              asChild
              defaultOpen={itemActive}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    isActive={itemActive}
                    className={cn(
                      activeBar,
                      "data-[active=true]:bg-sidebar-accent/90"
                    )}
                    tooltip={item.title}
                    onClick={(e) => {
                      if (state === "collapsed") {
                        e.preventDefault()
                        navigate({ to: item.url })
                      }
                    }}
                  >
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                    <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items?.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton
                          asChild
                          isActive={pathMatches(pathname, subItem.url)}
                        >
                          <Link to={subItem.url}>
                            <span>{subItem.title}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          )
        })}

      </SidebarMenu>
    </SidebarGroup>
  )
}

export function pathMatches(pathname: string, url: string) {
  if (url === "/") return pathname === "/"
  return pathname === url || pathname.startsWith(`${url}/`)
}
